import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../Context/UserContext";
import useAPI from "../../Hooks/useAPI";
import Header2 from "../../Components/Header2/Header2";
import { default as Footer } from "../../Components/Footer/FooterSmall";
import {
  faArrowLeft,
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../../Components/Page/Page.css";
import "./ContactManagement.css";
import * as XLSX from "xlsx";

interface Contact {
  cidn: number;
  ipaddress: string;
  firstname: string;
  lastname: string;
  middlename?: string;
  contacttype?: string;
  profession: string[];
  profession_other?: string;
  interests: string[];
  interests_other?: string;
  valid: boolean;
  email: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  companyname?: string;
  entitytype?: string;
  title?: string;
  phonenumber: string;
  dateadded: string;
  dateupdated: string;
  hasuser: boolean;
}

export default function ContactManagement() {
  const { setUser: setUserContext } = useUser();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { user, hasRole, hasPermission } = useUser();
  const navigate = useNavigate();
  const { getContacts, deleteContact } = useAPI();

  const [selectedName, setSelectedName] = useState(() => {
    return localStorage.getItem("nameFilter") || "";
  });

  const [sortConfig, setSortConfig] = useState<{
    key: keyof Contact | null;
    direction: "ascending" | "descending" | null;
  }>({
    key: null,
    direction: null,
  });
  const [hideInvalidContacts, setHideInvalidContacts] =
    useState<boolean>(false);

  const fetchContacts = useCallback(
    async (selName: string) => {
      try {
        setIsLoading(true);
        setError("");
        const response = await getContacts(selName);

        if (response?.data) {
          setContacts(response.data);
        } else {
          throw new Error("Failed to fetch contacts");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setIsLoading(false);
      }
    },
    [getContacts],
  );

  useEffect(() => {
    // Check if user is logged in
    if (!user) {
      navigate("/login");
      return;
    }

    // Check if user has administrator role
    if (!hasRole("administrator")) {
      navigate("/");
      return;
    }

    if (!hasPermission("manage_contacts")) {
      navigate("/");
      return;
    }

    fetchContacts(selectedName);
  }, [user, navigate, fetchContacts, hasRole, selectedName]);

  useEffect(() => {
    localStorage.setItem("nameFilter", selectedName);
  }, [selectedName]);

  const handleClear = () => {
    setSelectedName(""); // clears input
    localStorage.setItem("nameFilter", ""); // ensures blank is saved
  };

  const handleView = (cidn: number) => {
    navigate(`/contact-management/view/${cidn}`);
  };

  const handleAdd = () => {
    navigate("/contact-management/add");
  };

  const handleMatch = () => {
    navigate("/contact-management/Match");
  };

  const handleDelete = async (
    contactFN: string,
    contactLN: string,
    cidn: number,
    email: string,
  ) => {
    if (
      window.confirm(
        `Are you sure you want to delete the contact "${contactFN} ${contactLN}"? This action cannot be undone.`,
      )
    ) {
      try {
        const addedbyID = user?.id ?? "admin";

        await deleteContact(cidn, addedbyID, email);
        // Refresh the contacts list after successful deletion
        fetchContacts(selectedName);
      } catch (error) {
        setError(
          error instanceof Error ? error.message : "Failed to delete contact",
        );
      }
    }
  };

  const handleSort = (key: keyof Contact) => {
    let direction: "ascending" | "descending" | null = "ascending";

    if (sortConfig.key === key) {
      if (sortConfig.direction === "ascending") {
        direction = "descending";
      } else if (sortConfig.direction === "descending") {
        direction = null;
      }
    }

    setSortConfig({ key, direction });
  };

  const getSortedContacts = () => {
    let filteredContacts = contacts;

    // Filter out inactive contacts if toggle is enabled
    if (hideInvalidContacts) {
      filteredContacts = contacts.filter((user) => user.valid === true);
    }

    if (!sortConfig.key || !sortConfig.direction) {
      return filteredContacts;
    }

    return [...filteredContacts].sort((a, b) => {
      const aValue = a[sortConfig.key!];
      const bValue = b[sortConfig.key!];

      // Handle different data types
      let aStr: string;
      let bStr: string;

      if (Array.isArray(aValue)) {
        aStr = aValue.join(", ");
        bStr = Array.isArray(bValue) ? bValue.join(", ") : "";
      } else if (typeof aValue === "boolean") {
        aStr = aValue ? "Yes" : "No";
        bStr = typeof bValue === "boolean" ? (bValue ? "Yes" : "No") : "";
      } else if (typeof aValue === "number") {
        aStr = aValue.toString();
        bStr = typeof bValue === "number" ? bValue.toString() : "";
      } else {
        aStr = aValue ? aValue.toString().toLowerCase() : "";
        bStr = bValue ? bValue.toString().toLowerCase() : "";
      }

      if (aStr < bStr) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (aStr > bStr) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (columnKey: keyof Contact) => {
    if (sortConfig.key !== columnKey) {
      return <FontAwesomeIcon icon={faSort} />;
    }
    return sortConfig.direction === "ascending" ? (
      <FontAwesomeIcon icon={faSortUp} />
    ) : (
      <FontAwesomeIcon icon={faSortDown} />
    );
  };

  const calculateStatistics = () => {
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const startOfYear = new Date(now.getFullYear(), 0, 1);

    // Use filtered contacts for statistics if hiding invalid contacts
    const contactsForStats = hideInvalidContacts
      ? contacts.filter((user) => user.valid === true)
      : contacts;

    const totalContacts = contactsForStats.length;
    const activeContacts = contactsForStats.filter(
      (user) => user.valid === true,
    ).length;
    const contactsThisWeek = contactsForStats.filter(
      (user) => new Date(user.dateadded) >= oneWeekAgo,
    ).length;
    const contactsThisMonth = contactsForStats.filter(
      (user) => new Date(user.dateadded) >= oneMonthAgo,
    ).length;
    const contactsThisYear = contactsForStats.filter(
      (user) => new Date(user.dateadded) >= startOfYear,
    ).length;

    return {
      totalContacts,
      activeContacts,
      contactsThisWeek,
      contactsThisMonth,
      contactsThisYear,
    };
  };

  const handleExportToSpreadsheet = () => {
    // Prepare data for export
    const exportData = contacts.map((c) => ({
      "First Name": c.firstname,
      "Middle Name": c.middlename || "",
      "Last Name": c.lastname,
      Email: c.email,
      Address1: c.address1 || "",
      Address2: c.address2 || "",
      City: c.city || "",
      State: c.state || "",
      Zip: c.zip || "",
      Country: c.country || "",
      "Company Name": c.companyname || "",
      "Company Type": c.entitytype || "",
      Title: c.title || "",
      Phone: c.phonenumber || "",
      Profession: Array.isArray(c.profession) ? c.profession.join(", ") : "",
      "Profession Other": c.profession_other || "",
      Interests: Array.isArray(c.interests) ? c.interests.join(", ") : "",
      "Interests Other": c.interests_other || "",
      "Date Added": new Date(c.dateadded).toLocaleString(),
      "Last Updated": new Date(c.dateupdated).toLocaleString(),
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Contacts");

    // Generate filename with current date
    const currentDate = new Date().toISOString().split("T")[0];
    const filename = `IceGems_Contacts_export_${currentDate}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  return (
    <div className="generalPage">
      <Header2 />
      <div className="contactmanagement-container">
        <div className="pageContainer">
          <div className="button-container">
            <button className="back-button" onClick={() => navigate("/")}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
            </button>
          </div>

          <div className="contactmanagement-header">
            <div className="contactmanagement-actions">
              <h1 className="contactmanagement-title">Contact Management</h1>
              <div className="header-controls">
                <button
                  onClick={handleExportToSpreadsheet}
                  className="export-button"
                  disabled={isLoading || contacts.length === 0}
                >
                  Export to Spreadsheet
                </button>
              </div>
            </div>
          </div>

          {!isLoading && contacts.length > 0 && (
            <div className="statistics-container">
              <div className="statistics-grid">
                <div className="stat-item">
                  <div className="stat-number">
                    {calculateStatistics().totalContacts}
                  </div>
                  <div className="stat-label">Total Contacts</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">
                    {calculateStatistics().activeContacts}
                  </div>
                  <div className="stat-label">Valid Contacts</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">
                    {calculateStatistics().contactsThisWeek}
                  </div>
                  <div className="stat-label">Added Last 7 Days</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">
                    {calculateStatistics().contactsThisMonth}
                  </div>
                  <div className="stat-label">Added Last 30 Days</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">
                    {calculateStatistics().contactsThisYear}
                  </div>
                  <div className="stat-label">Added This Year</div>
                </div>
              </div>
            </div>
          )}

          <div className="contactmanagement-actions">
            <button onClick={handleAdd} className="add-user-button">
              Add New Contact
            </button>
            <button onClick={handleMatch} className="add-user-button">
              Contact Matching
            </button>
          </div>

          <div style={{ marginLeft: 0, marginRight: "auto" }}>
            <label className="marginright marginleft">
              Search Name / Email
            </label>
            <input
              type="text"
              name="nameFilter"
              value={selectedName}
              onChange={(e) => setSelectedName(e.target.value)}
              placeholder="Search..."
            />
            {selectedName && (
              <button
                type="button"
                onClick={handleClear}
                className="clear-button"
              >
                Clear
              </button>
            )}
          </div>
          {error && <div className="error-message">{error}</div>}

          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <div className="loading-text">Loading...</div>
            </div>
          ) : (
            <div className="contacts-table-container">
              <table className="contacts-table">
                <thead className="contacts-table-header">
                  <tr>
                    <th
                      onClick={() => handleSort("lastname")}
                      className="sortable-header"
                    >
                      Name {getSortIcon("lastname")}
                    </th>
                    <th
                      onClick={() => handleSort("email")}
                      className="sortable-header"
                    >
                      Email {getSortIcon("email")}
                    </th>
                    <th
                      onClick={() => handleSort("dateadded")}
                      className="sortable-header"
                    >
                      Date Added {getSortIcon("dateadded")}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedContacts().map((user, index) => (
                    <tr
                      key={`${user.cidn}-${user.email}-${index}`}
                      className="table-row"
                    >
                      <td>
                        {user.lastname}, {user.firstname}
                      </td>
                      <td>{user.email}</td>
                      <td>{new Date(user.dateadded).toLocaleDateString()}</td>
                      <td>
                        <button
                          onClick={() => handleView(user.cidn)}
                          className="action-button view-button"
                        >
                          View
                        </button>

                        {Boolean(user.hasuser) ? (
                          <></>
                        ) : (
                          <>
                            <button
                              onClick={() =>
                                handleDelete(
                                  user.firstname,
                                  user.lastname,
                                  user.cidn,
                                  user.email,
                                )
                              }
                              className="action-button delete-button"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
